// src/components/ProcessingResults/ShareJobModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { Share2, Users, CheckCircle, XCircle, Globe, Crown } from 'lucide-react';
import { groupsAPI } from '../../api/groupsApi';
import { sharePhotoJob, getPhotoJobShareInfo, unsharePhotoJob } from '../../api/photoProcessingApi';

const ShareJobModal = ({ show, onHide, job, onShareUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [shareInfo, setShareInfo] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('view');
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load user's groups and current share info
  useEffect(() => {
    if (show && job) {
      loadGroups();
      loadShareInfo();
    }
  }, [show, job]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const myGroups = await groupsAPI.getMyGroups();
      setGroups(myGroups || []);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError('Failed to load your groups');
    } finally {
      setLoading(false);
    }
  };

  const loadShareInfo = async () => {
    try {
      const info = await getPhotoJobShareInfo(job.id);
      console.log('Share info received:', info);
      setShareInfo(info);
    } catch (err) {
      console.error('Error loading share info:', err);
      setError('Failed to load sharing information');
    }
  };

  const handleShare = async () => {
    if (!selectedGroup) {
      setError('Please select a group to share with');
      return;
    }

    try {
      setSharingInProgress(true);
      setError(null);

      await sharePhotoJob(job.id, {
        group_id: selectedGroup,
        permission_level: permissionLevel
      });

      setSuccess(`Job shared successfully with ${getGroupName(selectedGroup)}`);

      // Refresh share info
      await loadShareInfo();

      // Notify parent component
      if (onShareUpdated) {
        onShareUpdated();
      }

      // Reset selection
      setSelectedGroup('');
      setPermissionLevel('view');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error sharing job:', err);
      setError(err.response?.data?.error || 'Failed to share job');
    } finally {
      setSharingInProgress(false);
    }
  };

  const handleUnshare = async (groupId) => {
    if (!window.confirm('Remove access for this group?')) return;

    try {
      setSharingInProgress(true);
      await unsharePhotoJob(job.id, { group_id: groupId });
      await loadShareInfo();
      if (onShareUpdated) onShareUpdated();
      setSuccess('Group access removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing access:', err);
      setError(err.response?.data?.error || 'Failed to remove group access');
    } finally {
      setSharingInProgress(false);
    }
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Unknown Group';
  };

  const getPermissionBadge = (level) => {
    if (!level) return <Badge bg="secondary">UNKNOWN</Badge>;
    const variants = {
      view: 'info',
      edit: 'warning',
      admin: 'danger'
    };
    return <Badge bg={variants[level.toLowerCase()] || 'secondary'}>{level.toUpperCase()}</Badge>;
  };

  // Get shared groups from access_list (for owners/admins) or accessible_groups (for regular users)
  const getSharedGroups = () => {
    if (!shareInfo) return [];

    // For owners/admins, use access_list
    if (shareInfo.access_list && shareInfo.access_list.length > 0) {
      return shareInfo.access_list;
    }

    // For regular users, use accessible_groups
    if (shareInfo.accessible_groups && shareInfo.accessible_groups.length > 0) {
      return shareInfo.accessible_groups;
    }

    return [];
  };

  // Filter out groups that already have access
  const getAvailableGroups = () => {
    const sharedGroups = getSharedGroups();
    const sharedGroupIds = new Set(sharedGroups.map(sg => sg.group_id || sg.id));

    return groups.filter(group => !sharedGroupIds.has(group.id));
  };

  const sharedGroups = getSharedGroups();
  const availableGroups = getAvailableGroups();
  const isOwner = shareInfo?.is_owner || false;
  const userPermission = shareInfo?.user_permission;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Share2 size={20} className="me-2" />
          Share Analysis Job: {job?.original_image_filename || job?.id}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            {success}
          </Alert>
        )}

        {/* Current sharing info */}
        <div className="mb-4">
          <h6 className="mb-3">
            <Users size={16} className="me-1" />
            Current Access
          </h6>

          {/* Owner info */}
          {isOwner && (
            <div className="mb-2 p-2 bg-light rounded">
              <div className="d-flex align-items-center">
                <Crown size={16} className="text-warning me-2" />
                <div>
                  <small className="text-muted">You are the owner</small>
                  <div className="mt-1">
                    <Badge bg="danger">OWNER</Badge>
                    <span className="ms-2 text-muted small">Full control</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User's permission level (if not owner) */}
          {userPermission && !isOwner && (
            <div className="mb-2 p-2 bg-light rounded">
              <small className="text-muted">Your permission level</small>
              <div className="mt-1">
                {getPermissionBadge(userPermission)}
              </div>
            </div>
          )}

          {/* Shared groups list */}
          {sharedGroups.length > 0 && (
            <div className="mt-3">
              <strong>Shared with groups:</strong>
              <ListGroup className="mt-2">
                {sharedGroups.map((sg, idx) => (
                  <ListGroup.Item key={idx} className="d-flex justify-content-between align-items-center">
                    <div>
                      <Users size={14} className="me-2 text-muted" />
                      {sg.name || sg.group_name}
                      <span className="ms-2">
                        {getPermissionBadge(sg.permission_level || sg.your_permission)}
                      </span>
                      {sg.shared_by && (
                        <small className="text-muted ms-2">
                          (shared by {sg.shared_by})
                        </small>
                      )}
                    </div>
                    {isOwner && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => handleUnshare(sg.group_id || sg.id)}
                        disabled={sharingInProgress}
                      >
                        <XCircle size={16} />
                      </Button>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {/* No sharing info */}
          {sharedGroups.length === 0 && !isOwner && userPermission && (
            <div className="text-center py-3 text-muted">
              <Globe size={32} className="mb-2" />
              <p>This job is not shared with any groups</p>
              <small>Only you can access it</small>
            </div>
          )}

          {sharedGroups.length === 0 && isOwner && (
            <div className="text-center py-3 text-muted">
              <Globe size={32} className="mb-2" />
              <p>This job is not shared with any groups yet</p>
              <small>Share it with groups below to collaborate</small>
            </div>
          )}
        </div>

        {/* Share with new group - only for owners */}
        {isOwner && (
          <>
            <hr />
            <h6 className="mb-3">Share with a group</h6>

            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : availableGroups.length === 0 ? (
              <Alert variant="info">
                <Users size={16} className="me-2" />
                {groups.length === 0 ? (
                  <>You don't have any groups yet. <a href="/groups" className="alert-link">Create a group</a> first.</>
                ) : (
                  <>All your groups already have access to this job.</>
                )}
              </Alert>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Select Group</Form.Label>
                  <Form.Select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                  >
                    <option value="">Choose a group...</option>
                    {availableGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.members_count || 0} members)
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Permission Level</Form.Label>
                  <Form.Select
                    value={permissionLevel}
                    onChange={(e) => setPermissionLevel(e.target.value)}
                  >
                    <option value="view">View Only - Can see job results and adjustments</option>
                    <option value="edit">Edit - Can modify adjustments and save changes</option>
                    <option value="admin">Admin - Full control (except deleting)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {permissionLevel === 'view' && 'Members can view job results and adjustments'}
                    {permissionLevel === 'edit' && 'Members can modify and save adjustments'}
                    {permissionLevel === 'admin' && 'Members have full control over the job'}
                  </Form.Text>
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={handleShare}
                  disabled={!selectedGroup || sharingInProgress}
                  className="w-100"
                >
                  {sharingInProgress ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sharing...
                    </>
                  ) : (
                    <>
                      <Share2 size={16} className="me-2" />
                      Share with Group
                    </>
                  )}
                </Button>
              </>
            )}
          </>
        )}

        {/* Non-owner view */}
        {!isOwner && userPermission && (
          <div className="text-center py-3 text-muted">
            <CheckCircle size={32} className="text-success mb-2" />
            <p>You have {userPermission} access to this job</p>
            <small>Contact the owner to change sharing settings</small>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareJobModal;